import { useCallback, useEffect, useRef, useState } from "react";

import {
  ApiGetRestrictionBreakdown,
  RestrictionBreakdownResponse,
} from "../../../services/api";

interface UseRestrictionBreakdownArgs {
  layerName: string | null;
  geometry: GeoJSON.Geometry | null;
  /** Altura do rotor no modo eólico; os cards de área propícia usam a mesma. */
  hubHeightM?: number;
}

export interface UseRestrictionBreakdownResult {
  breakdown: RestrictionBreakdownResponse | null;
  isOpen: boolean;
  isLoading: boolean;
  error: string | null;
  open: () => void;
  close: () => void;
}

/**
 * Memorial de Cálculo da Área Propícia: carrega o detalhamento sob demanda.
 *
 * O cálculo é caro (um raster de potencial por nível mais uma máscara por
 * restrição), por isso só dispara quando o usuário pede — mas, uma vez aberto,
 * o memorial acompanha a troca de nível de projeção: o clique é um pedido pelo
 * modo memorial, não por um resultado único, e comparar níveis é justamente o
 * que se faz depois de abrir o primeiro.
 *
 * Trocar de área é intenção nova, não continuação: o memorial fecha e precisa
 * ser pedido outra vez para a nova seleção.
 */
export function useRestrictionBreakdown({
  layerName,
  geometry,
  hubHeightM,
}: UseRestrictionBreakdownArgs): UseRestrictionBreakdownResult {
  // A área para a qual o memorial foi pedido. Guardar a geometria, em vez de um
  // booleano mais um efeito que fecha quando ela muda, elimina a corrida entre
  // os dois efeitos: com um booleano, o efeito de fechar e o de buscar rodam no
  // mesmo commit e o segundo ainda enxerga isOpen=true, refazendo a busca para a
  // área nova em vez de fechar. Aqui "aberto" é derivado, não sincronizado.
  const [openedForGeometry, setOpenedForGeometry] =
    useState<GeoJSON.Geometry | null>(null);
  const [breakdown, setBreakdown] = useState<RestrictionBreakdownResponse | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Preso à geometria, não à camada: trocar de nível mantém o memorial aberto e
  // refaz o cálculo (é justamente o que se faz depois de abrir o primeiro). Mas
  // layerName nulo é seleção inválida — trocar de modo zera a projeção sem
  // trocar a área, e sem esta guarda o memorial da camada antiga continuaria na
  // tela com o botão de fechar já escondido.
  const isOpen =
    openedForGeometry !== null &&
    openedForGeometry === geometry &&
    layerName !== null;

  // Descarta respostas de pedidos superados — trocar de nível rápido faria uma
  // resposta antiga sobrescrever a atual sem esta guarda.
  const requestIdRef = useRef(0);

  const close = useCallback(() => {
    requestIdRef.current += 1;
    setOpenedForGeometry(null);
    setBreakdown(null);
    setError(null);
    setIsLoading(false);
  }, []);

  const open = useCallback(() => setOpenedForGeometry(geometry), [geometry]);

  useEffect(() => {
    if (isOpen) return;
    // Fechado (por clique, por troca de área ou por seleção inválida): além de
    // descartar os dados da seleção anterior, esquece a área pedida e invalida
    // o pedido em voo. Sem isso, uma seleção que volta a ser válida (trocar de
    // modo zera a projeção e o usuário escolhe outra na mesma área) faria
    // isOpen voltar a true sozinho, reabrindo o memorial sem novo pedido.
    requestIdRef.current += 1;
    setOpenedForGeometry(null);
    setBreakdown(null);
    setError(null);
    setIsLoading(false);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !layerName || !geometry) return;

    const requestId = ++requestIdRef.current;
    setIsLoading(true);
    setError(null);

    ApiGetRestrictionBreakdown({
      layer_name: layerName,
      geometry,
      hub_height_m: hubHeightM,
    })
      .then((data) => {
        if (requestId !== requestIdRef.current) return;
        setBreakdown(data);
      })
      .catch((err: unknown) => {
        if (requestId !== requestIdRef.current) return;
        setBreakdown(null);
        setError(
          err instanceof Error
            ? err.message
            : "Não foi possível calcular o memorial desta área."
        );
      })
      .finally(() => {
        if (requestId !== requestIdRef.current) return;
        setIsLoading(false);
      });
  }, [isOpen, layerName, geometry, hubHeightM]);

  return { breakdown, isOpen, isLoading, error, open, close };
}
